<template>
  <div class="page mechanism">
    <div class="kpi-row">
      <div class="kpi" @click="openPointsRule">
        <div class="kpi__left">
          <span class="kpi__icon-wrap">
            <i class="ri-medal-fill kpi__icon" aria-hidden="true" />
          </span>
          <span class="kpi__label">总积分</span>
        </div>
        <b>{{ kpiInt("totalPoints") }}<em>分</em></b>
      </div>
      <div class="kpi kpi--green" @click="openActiveMembers">
        <div class="kpi__left">
          <span class="kpi__icon-wrap kpi__icon-wrap--green">
            <i class="ri-user-star-fill kpi__icon" aria-hidden="true" />
          </span>
          <span class="kpi__label">月活跃会员数</span>
        </div>
        <b>{{ kpiInt("activeMembers") }}<em>人</em></b>
      </div>
      <div class="kpi kpi--orange" @click="openResponseDetail">
        <div class="kpi__left">
          <span class="kpi__icon-wrap kpi__icon-wrap--orange">
            <i class="ri-timer-flash-fill kpi__icon" aria-hidden="true" />
          </span>
          <span class="kpi__label">三单响应时长</span>
        </div>
        <b>{{ responseHoursDisp }}<em>小时</em></b>
      </div>
      <div class="kpi" @click="openParticipate">
        <div class="kpi__left">
          <span class="kpi__icon-wrap">
            <i class="ri-pie-chart-2-fill kpi__icon" aria-hidden="true" />
          </span>
          <span class="kpi__label">会员参与率</span>
        </div>
        <b>{{ kpiInt("participateRate") }}<em>%</em></b>
      </div>
    </div>

    <div class="cols">
      <div class="col col--left">
        <section class="panel">
          <h3>三单服务流程</h3>
          <div class="flow">
            <div
              v-for="(n, i) in flowNodes"
              :key="n.key"
              class="flow__step"
            >
              <button
                type="button"
                class="flow__node"
                :class="'tone-' + n.tone"
                @click="openFlowNode(n)"
              >
                <strong>{{ n.label }}</strong>
                <em>{{ n.value }}</em>
                <span>{{ n.unit }}</span>
              </button>
              <div
                v-if="i < flowNodes.length - 1"
                class="flow__link"
                aria-hidden="true"
              >
                <i class="flow__link-track" />
                <i class="flow__link-beam" />
              </div>
            </div>
          </div>
        </section>

        <section class="panel panel--rank">
          <h3>积分 TOP10</h3>
          <ol class="rank">
            <li
              v-for="(p, i) in topPoints"
              :key="i"
              :class="'medal-' + Math.min(p.rank || i + 1, 4)"
              @click="openTopPerson(p)"
            >
              <span
                class="rank__no"
                :class="{
                  'rank__medal': Math.min(p.rank || i + 1, 4) <= 3
                }"
              >{{ p.rank || i + 1 }}</span>
              <div class="rank__avatar" :style="avatarStyle(p)">
                <span v-if="!avatarUrlOf(p)">{{ nameInitial(p) }}</span>
              </div>
              <div class="rank__meta">
                <strong>{{ p.name }}</strong>
                <em v-if="p.unit">{{ p.unit }}</em>
              </div>
              <b>{{ p.points }}</b>
            </li>
            <li v-if="!topPoints.length" class="empty">暂无积分数据</li>
          </ol>
        </section>
      </div>

      <div class="col col--right">
        <section class="panel panel--chart">
          <h3>积分分布</h3>
          <div ref="bucketChart" class="chart"></div>
        </section>

        <section class="panel panel--honor">
          <h3>特色 / 荣誉</h3>
          <p v-if="!honorSlides.length" class="empty">暂无内容，请在后台「轮播管理」维护</p>
          <div
            v-else
            class="honor-carousel"
            @mouseenter="honorPaused = true"
            @mouseleave="honorPaused = false"
          >
            <div class="honor-carousel__viewport">
              <div class="honor-carousel__track" :style="honorTrackStyle">
                <button
                  v-for="(s, i) in honorSlides"
                  :key="s._slideKey"
                  type="button"
                  class="honor-slide"
                  @click="openHonor(s, i)"
                >
                  <div
                    class="honor-slide__img"
                    :style="
                      s.coverUrl
                        ? { backgroundImage: 'url(' + absUrl(s.coverUrl) + ')' }
                        : {}
                    "
                  />
                  <div
                    class="honor-slide__body"
                    :class="{ 'is-large': s.isFeatured }"
                  >
                    <em v-if="s.category" class="honor-slide__cat">{{ s.category }}</em>
                    <strong>{{ s.title }}</strong>
                    <p>{{ s.content || "—" }}</p>
                  </div>
                </button>
              </div>
            </div>
            <div v-if="honorSlides.length > 1" class="honor-carousel__dots">
              <button
                v-for="(s, i) in honorSlides"
                :key="'dot-' + s._slideKey"
                type="button"
                class="honor-carousel__dot"
                :class="{ on: honorIndex === i }"
                :aria-label="'第 ' + (i + 1) + ' 张'"
                @click="goHonorSlide(i)"
              />
            </div>
          </div>
        </section>
      </div>
    </div>

    <div v-if="lightbox.open" class="lb" @click.self="closeLightbox">
      <button type="button" class="lb__nav lb__prev" @click="lightboxPrev">‹</button>
      <div class="lb__body">
        <img
          v-if="lbItem.coverUrl"
          :src="absUrl(lbItem.coverUrl)"
          :alt="lbItem.title"
        />
        <div v-else class="lb__placeholder">{{ lbItem.title }}</div>
        <p>{{ lbItem.title }}</p>
      </div>
      <button type="button" class="lb__nav lb__next" @click="lightboxNext">›</button>
      <button type="button" class="lb__close" @click="closeLightbox">×</button>
    </div>
  </div>
</template>

<script>
import { BarChart } from "@/plugins/echarts_bank/index";
import branchQuery from "@/utils/branchQuery";
import { absMediaUrl, toDetailItem, memberAvatarUrl, memberAvatarStyle, memberNameInitial } from "@/utils/mediaUrl";
import { isSameJson } from "@/utils/stableData";
import dashPage from "@/mixins/dashPage";

export default {
  name: "DashMechanism",
  mixins: [branchQuery, dashPage],
  data() {
    return {
      payload: {},
      chart: null,
      honorIndex: 0,
      honorPaused: false,
      honorTimer: null,
      bucketsSig: null,
      honorSig: null
    };
  },
  computed: {
    kpis() {
      return this.payload.kpis || {};
    },
    flow() {
      return this.payload.flow || {};
    },
    flowDetail() {
      return this.payload.flowDetail || {};
    },
    flowNodes() {
      const f = this.flow;
      return [
        {
          key: "orders",
          label: "群众点单",
          value: f.orders ?? "—",
          unit: "单",
          tone: "red",
          detail: "点单总量、状态分布与近期点单"
        },
        {
          key: "dispatch",
          label: "中心派单",
          value: f.dispatchMinutes ?? "—",
          unit: "分钟",
          tone: "blue",
          detail: "点单→派单平均时长（分钟）"
        },
        {
          key: "accept",
          label: "会员接单",
          value: f.acceptRate ?? "—",
          unit: "%",
          tone: "green",
          detail: "会员接单率"
        },
        {
          key: "praise",
          label: "群众评单",
          value: f.praiseRate ?? "—",
          unit: "%",
          tone: "gold",
          detail: "群众好评率"
        }
      ];
    },
    topPoints() {
      return this.payload.topPoints || [];
    },
    activeMembersList() {
      return this.payload.activeMembersList || [];
    },
    honors() {
      return this.payload.honors || [];
    },
    /** 封面 + 补充图拆成轮播帧（建议 2～3 张） */
    honorSlides() {
      const slides = [];
      (this.honors || []).forEach((h, hi) => {
        const urls = [];
        const add = u => {
          const url = (u || "").trim();
          if (!url || urls.indexOf(url) >= 0) return;
          urls.push(url);
        };
        add(h.coverUrl || h.CoverUrl);
        const gallery = h.galleryUrls || h.GalleryUrls || [];
        if (Array.isArray(gallery)) gallery.forEach(add);
        const raw = h.galleryText || h.GalleryText || "";
        if (raw) {
          String(raw)
            .split(/[\n,;|，；]+/)
            .forEach(add);
        }
        const base = {
          title: h.title || "",
          content: (h.content || "").trim(),
          category: h.category || "",
          time: h.time || ""
        };
        if (!urls.length) {
          slides.push({
            ...base,
            coverUrl: "",
            _slideKey: hi + "-0",
            _honorIndex: hi
          });
          return;
        }
        urls.forEach((url, ui) => {
          slides.push({
            ...base,
            coverUrl: url,
            _slideKey: hi + "-" + ui,
            _honorIndex: hi
          });
        });
      });
      return slides;
    },
    honorTrackStyle() {
      return {
        transform: `translateX(-${this.honorIndex * 100}%)`
      };
    },
    responseHoursDisp() {
      const v = this.displayKpi.avgResponseHours;
      if (v == null || Number.isNaN(v)) return "—";
      return (Math.round(v * 10) / 10).toFixed(1);
    },
    lbItem() {
      return this.lightbox.list[this.lightbox.index] || {};
    }
  },
  watch: {
    branchQuery: {
      deep: true,
      handler() {
        this.load();
      }
    },
    payload() {
      this.redrawIfChanged("bucketsSig", this.payload.pointBuckets || [], () => {
        this.$nextTick(this.renderBuckets);
      });
      const k = this.payload.kpis || {};
      this.syncRollKpis({
        totalPoints: k.totalPoints || 0,
        activeMembers: k.activeMembers || 0,
        avgResponseHours: Number(k.avgResponseHours) || 0,
        participateRate: k.participateRate || 0
      });
    },
    honorSlides(list) {
      if (isSameJson(list, this.honorSig)) return;
      this.honorSig = list;
      this.honorIndex = 0;
      this.startHonorAutoplay();
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.chart = new BarChart({
        ele: this.$refs.bucketChart,
        fontSize: this.commonFont,
        fontColor: "#B8C7FF",
        palletColors: ["#5ad6ff", "#36d7b7", "#f5c542", "#ff8a3d"],
        mAxis: "yAxis",
        vAxis: "xAxis",
        unit: "人",
        showUnitMethod: 2,
        barBorderRadius: [0, 4, 4, 0],
        grid: { top: 8, right: 28, bottom: 8, left: 8, containLabel: true },
        series: [
          {
            type: "bar",
            name: "会员数",
            barWidth: "48%",
            label: {
              show: true,
              position: "right",
              fontSize: Math.max(11, (this.commonFont || 14) - 2),
              color: "#e8f4ff",
              formatter: "{c} 人"
            }
          }
        ]
      });
      if (this.payload && this.payload.pointBuckets) this.renderBuckets();
      this.load();
      this.startPageRefresh();
      this.startHonorAutoplay();
    });
  },
  beforeDestroy() {
    this.stopHonorAutoplay();
    this.unbindBucketClick();
    if (this.chart) this.chart.destroy();
  },
  methods: {
    absUrl: absMediaUrl,
    avatarUrlOf(p) {
      return memberAvatarUrl(p);
    },
    avatarStyle(p) {
      return memberAvatarStyle(p);
    },
    nameInitial(p) {
      return memberNameInitial(p);
    },
    async load() {
      const res = await this.$api("screen", "mechanism", this.branchQuery);
      if (res && res.code === 0) {
        if (isSameJson(this.payload, res)) return;
        this.payload = res;
      }
    },
    bucketColors() {
      return ["#5ad6ff", "#36d7b7", "#f5c542", "#ff8a3d"];
    },
    renderBuckets() {
      if (!this.chart) return;
      const buckets = this.payload.pointBuckets || [];
      const colors = this.bucketColors();
      const data = (buckets.length
        ? buckets
        : [
            { name: "0-100", label: "新会员", value: 0 },
            { name: "100-500", label: "普通会员", value: 0 },
            { name: "500-1000", label: "活跃会员", value: 0 },
            { name: "1000+", label: "骨干会员", value: 0 }
          ]
      ).map((b, i) => ({
        name: `${b.name} ${b.label || ""}`.trim(),
        value: Number(b.value) || 0,
        bucketKey: b.name,
        itemStyle: {
          color: colors[i % colors.length]
        }
      }));
      // BarChart 要求 data 为「系列数组」：[[{name,value}...]]
      this.chart.setData([data]).draw();
      this.$nextTick(() => {
        if (this.chart && this.chart.resize) this.chart.resize();
        this.bindBucketClick();
      });
    },
    unbindBucketClick() {
      const ec = this.chart && this.chart._chart;
      if (ec) ec.off("click");
    },
    bindBucketClick() {
      const ec = this.chart && this.chart._chart;
      if (!ec) return;
      ec.off("click");
      ec.on("click", params => {
        if (!params || params.componentType !== "series") return;
        const buckets = this.payload.pointBuckets || [];
        const idx = params.dataIndex;
        const bucket = buckets[idx];
        if (!bucket) return;
        this.openBucketMembers(bucket);
      });
    },
    bucketLabel(name) {
      const map = {
        "0-100": "新会员",
        "100-500": "普通会员",
        "500-1000": "活跃会员",
        "1000+": "骨干会员"
      };
      return map[name] || "";
    },
    openBucketMembers(bucket) {
      const name = bucket.name || "";
      const label = bucket.label || this.bucketLabel(name);
      const members = bucket.members || bucket.Members || [];
      const count = members.length || Number(bucket.value) || 0;
      const title = `${name}${label ? " · " + label : ""}` || "积分段";
      this.openDrawer({
        title: `积分分布 · ${title}`,
        listLayout: "bucket",
        heroIcon: "ri-bar-chart-horizontal-fill",
        listHeading: "会员列表",
        summary: [
          {
            label: label ? `${label}人数` : "会员数",
            value: `${count} 人`
          },
          {
            label: label || "",
            value: name || "—"
          }
        ],
        list: members
      });
    },
    openPointsRule() {
      this.openDrawer({
        title: "积分规则说明",
        listLayout: "points",
        summary: [
          {
            label: "会员总积分",
            value: this.kpis.totalPoints || 0
          }
        ],
        list: [
          {
            icon: "ri-calendar-check-fill",
            color: "#3d8bff",
            title: "参加活动签到",
            points: 5
          },
          {
            icon: "ri-hand-heart-fill",
            color: "#36d7b7",
            title: "公益服务 1 小时",
            points: 5
          },
          {
            icon: "ri-lightbulb-flash-fill",
            color: "#f5c542",
            title: "建言被采纳",
            points: 50
          },
          {
            icon: "ri-file-list-3-fill",
            color: "#ff8a3d",
            title: "三单已评单并办结",
            points: 15
          },
          {
            icon: "ri-building-4-fill",
            color: "#f97316",
            title: "助企服务专家",
            points: 15
          },
          {
            icon: "ri-book-read-fill",
            color: "#a78bfa",
            title: "学习完成",
            points: 10
          }
        ]
      });
    },
    openActiveMembers() {
      const list = this.activeMembersList || [];
      this.openDrawer({
        title: "月活跃会员数",
        listLayout: "active",
        summary: [
          {
            label: "当月活跃",
            value: `${this.kpis.activeMembers || list.length || 0} 人`
          }
        ],
        list
      });
    },
    openResponseDetail() {
      const trend = this.payload.responseTrend || [];
      this.openDrawer({
        title: "响应时长趋势",
        listLayout: "response",
        summary: [
          {
            label: "当前平均响应",
            value:
              this.responseHoursDisp === "—"
                ? "—"
                : `${this.responseHoursDisp} 小时`
          }
        ],
        list: trend
      });
    },
    openParticipate() {
      const trend = this.payload.participateTrend || [];
      this.openDrawer({
        title: "会员参与率趋势",
        listLayout: "participate",
        summary: [
          {
            label: "当月参与率",
            value: `${this.kpis.participateRate || 0}%`
          }
        ],
        list: trend
      });
    },
    openFlowNode(n) {
      const d = this.flowDetail || {};
      if (n.key === "orders") {
        this.openOrdersFlow(d.orders || {});
        return;
      }
      if (n.key === "dispatch") {
        this.openDispatchFlow(d.dispatch || {});
        return;
      }
      if (n.key === "accept") {
        this.openAcceptFlow(d.accept || {});
        return;
      }
      if (n.key === "praise") {
        this.openPraiseFlow(d.praise || {});
        return;
      }
      this.openDrawer({
        title: `三单 · ${n.label}`,
        summary: [`${n.label}：${n.value}${n.unit}`],
        list: [n.detail]
      });
    },
    openOrdersFlow(orders) {
      const stats = orders.statusStats || [];
      const recent = orders.recent || [];
      const recentItems = recent.length
        ? recent.map(r =>
            toDetailItem({
              title: r.title,
              content: r.content,
              status: r.status,
              time: r.time,
              author: r.author
            })
          )
        : [
            toDetailItem({
              title: "近 3 个月暂无点单",
              content: "请在后台「三单服务」录入点单"
            })
          ];
      const total =
        orders.total != null ? orders.total : this.flow.orders || 0;
      this.openDrawer({
        title: "群众点单",
        listLayout: "order-status",
        summary: [{ label: "总点单数", value: `${total} 单` }],
        list: stats.map(s => ({
          name: s.name,
          value: Number(s.value) || 0
        })),
        sections: [{ title: "近 3 个月点单", items: recentItems }]
      });
    },
    openDispatchFlow(dispatch) {
      const rules = dispatch.rules || [];
      const recent = dispatch.recent || [];
      const ruleItems = (rules.length
        ? rules
        : [
            "群众提交点单后进入待派单池，由中心按专长 / 专委会匹配会员。",
            "状态只能逐步推进：已点单 → 已派单 → 已接单 → 已评单。",
            "派单时长 = 派单时间 − 点单时间（系统自动计算）。"
          ]
      ).map((text, i) =>
        toDetailItem({
          title: `规则 ${i + 1}`,
          content: text
        })
      );
      const recentItems = recent.length
        ? recent.map(r =>
            toDetailItem({
              title: r.title,
              content: r.content,
              category: r.category,
              status: r.status,
              author: r.author,
              time: r.time
            })
          )
        : [
            toDetailItem({
              title: "近 3 个月暂无派单",
              content: "推进至「已派单」并填写派单时间后，将展示派单留痕"
            })
          ];
      const avg =
        dispatch.avgMinutes != null
          ? dispatch.avgMinutes
          : this.flow.dispatchMinutes;
      this.openDrawer({
        title: "中心派单",
        listLayout: "flow-hero",
        heroIcon: "ri-send-plane-fill",
        summary: [
          {
            label: "平均派单时长",
            value: avg != null && avg !== "" ? `${avg} 分钟` : "—"
          }
        ],
        sections: [
          { title: "派单规则", items: ruleItems },
          { title: "近 3 个月派单", items: recentItems }
        ]
      });
    },
    openAcceptFlow(accept) {
      const rank = accept.serviceRank || [];
      const rankItems = rank.length
        ? rank.map(r =>
            toDetailItem({
              title: `${r.rank != null ? r.rank + ". " : ""}${r.title || r.name}`,
              content: r.content,
              category: r.category,
              author: r.author,
              meta: r.unit || r.meta
            })
          )
        : [
            toDetailItem({
              title: "暂无接单排行",
              content: "会员接单后将按接单次数与平均响应时长汇总排行"
            })
          ];
      const rate =
        accept.rate != null ? accept.rate : this.flow.acceptRate || 0;
      this.openDrawer({
        title: "会员接单",
        listLayout: "flow-hero",
        heroIcon: "ri-user-star-fill",
        summary: [{ label: "会员接单率", value: `${rate}%` }],
        sections: [{ title: "接单排行", items: rankItems }]
      });
    },
    openPraiseFlow(praise) {
      const picks = praise.picks || [];
      const pickItems = picks.length
        ? picks.map(p =>
            toDetailItem({
              title: p.title,
              content: p.content,
              category: p.category,
              status: p.status,
              author: p.author,
              time: p.time,
              rating: p.rating || 0
            })
          )
        : [
            toDetailItem({
              title: "暂无评价精选",
              content: "办结评单并填写群众评价后，将在此展示"
            })
          ];
      const rate =
        praise.rate != null ? praise.rate : this.flow.praiseRate || 0;
      this.openDrawer({
        title: "群众评单",
        listLayout: "flow-hero",
        heroIcon: "ri-star-smile-fill",
        summary: [{ label: "好评率", value: `${rate}%` }],
        sections: [{ title: "群众评价精选", items: pickItems }]
      });
    },
    openTopPerson(p) {
      const rank = p.rank;
      const name = p.name || "会员";
      // 大屏 breakdown 已把加减分并入活动/公益/建言，不单独展示调控项
      const raw = p.breakdown || p.Breakdown || {};
      const pick = (...keys) => {
        for (let i = 0; i < keys.length; i++) {
          const v = raw[keys[i]];
          if (v != null && v !== "") return Number(v) || 0;
        }
        return 0;
      };
      let activity = pick("activity", "Activity", "activityPoints", "ActivityPoints");
      let volunteer = pick("volunteer", "Volunteer", "volunteerPoints", "VolunteerPoints");
      let suggestion = pick(
        "suggestion",
        "Suggestion",
        "suggestionPoints",
        "SuggestionPoints"
      );
      let learning = pick("learning", "Learning", "learningPoints", "LearningPoints");
      let sandan = pick("sandan", "Sandan", "sandanPoints", "SandanPoints");
      let enterprise = pick(
        "enterprise",
        "Enterprise",
        "enterprisePoints",
        "EnterprisePoints"
      );
      // 兼容旧接口若仍下发 adjust：六项按规则单位轮询摊入
      const adjust = pick("adjust", "Adjust", "adjustPoints", "AdjustPoints");
      if (adjust) {
        const units = [5, 5, 50, 10, 15, 15];
        const scores = [
          activity,
          volunteer,
          suggestion,
          learning,
          sandan,
          enterprise
        ];
        let left = Math.abs(Math.trunc(adjust / 5) * 5);
        const sign = adjust > 0 ? 1 : -1;
        let i = 0;
        let idle = 0;
        while (left > 0 && idle < units.length) {
          const idx = i % units.length;
          const unit = units[idx];
          const can =
            sign > 0 ? left >= unit : left >= unit && scores[idx] >= unit;
          if (can) {
            scores[idx] += sign * unit;
            left -= unit;
            idle = 0;
          } else {
            idle++;
          }
          i++;
        }
        if (left > 0 && sign > 0) scores[0] += left;
        activity = Math.max(0, scores[0]);
        volunteer = Math.max(0, scores[1]);
        suggestion = Math.max(0, scores[2]);
        learning = Math.max(0, scores[3]);
        sandan = Math.max(0, scores[4]);
        enterprise = Math.max(0, scores[5]);
      }
      const total =
        pick("total", "Total", "totalPoints", "TotalPoints") ||
        Number(p.points) ||
        0;
      const hasBreakdown =
        !!p.breakdown ||
        !!p.Breakdown ||
        activity + volunteer + suggestion + learning + sandan + enterprise !== 0;

      const rows = [
        {
          icon: "ri-calendar-check-fill",
          color: "#3d8bff",
          title: "参加活动",
          points: activity
        },
        {
          icon: "ri-hand-heart-fill",
          color: "#36d7b7",
          title: "做公益",
          points: volunteer
        },
        {
          icon: "ri-lightbulb-flash-fill",
          color: "#f5c542",
          title: "提建言",
          points: suggestion
        },
        {
          icon: "ri-book-read-fill",
          color: "#a78bfa",
          title: "学习完成",
          points: learning
        },
        {
          icon: "ri-file-list-3-fill",
          color: "#ff8a3d",
          title: "三单办结",
          points: sandan
        },
        {
          icon: "ri-building-4-fill",
          color: "#f97316",
          title: "助企服务",
          points: enterprise
        }
      ];

      this.openDrawer({
        title: `${name} · 积分明细`,
        listLayout: "points",
        listHeading: hasBreakdown
          ? "积分构成（与积分管理同一规则）"
          : "积分构成未就绪：请重启 API 后刷新大屏",
        summary: [
          {
            label: `排名第 ${rank || "—"} · 总分`,
            value: total
          }
        ],
        list: rows
      });
    },
    openHonor(h, index) {
      if (h.coverUrl) {
        this.openLightbox(this.honorSlides, index);
        return;
      }
      const item = toDetailItem(h);
      this.openDrawer({ title: h.title || "", items: [item] });
    },
    goHonorSlide(i) {
      const n = this.honorSlides.length;
      if (!n) return;
      this.honorIndex = ((i % n) + n) % n;
    },
    startHonorAutoplay() {
      this.stopHonorAutoplay();
      if (this.honorSlides.length <= 1) return;
      this.honorTimer = setInterval(() => {
        if (this.honorPaused || (this.lightbox && this.lightbox.open)) return;
        const n = this.honorSlides.length;
        if (n <= 1) return;
        this.honorIndex = (this.honorIndex + 1) % n;
      }, 3000);
    },
    stopHonorAutoplay() {
      if (this.honorTimer) {
        clearInterval(this.honorTimer);
        this.honorTimer = null;
      }
    }
  }
};
</script>

<style lang="less" scoped>
.page {
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 10 / @rem2;
  min-height: 0;
}
.kpi-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10 / @rem2;
  flex-shrink: 0;
}
.kpi {
  min-height: 80 / @rem2;
  box-sizing: border-box;
  padding: 12 / @rem2 14 / @rem2;
  border: 1 / @rem2 solid fade(@mainColor, 35%);
  border-radius: 6 / @rem2;
  background: rgba(8, 28, 64, 0.55);
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10 / @rem2;
  cursor: pointer;
  &__left {
    display: flex;
    align-items: center;
    gap: 10 / @rem2;
    min-width: 0;
  }
  &__icon-wrap {
    position: relative;
    width: 36 / @rem2;
    height: 36 / @rem2;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    &::before {
      content: "";
      position: absolute;
      inset: 0;
      border-radius: 50%;
      background: radial-gradient(
        circle,
        fade(@mainColor, 32%) 0%,
        fade(@mainColor, 8%) 50%,
        transparent 72%
      );
    }
  }
  &__icon-wrap--green::before {
    background: radial-gradient(
      circle,
      fade(#36d7b7, 36%) 0%,
      fade(#36d7b7, 8%) 50%,
      transparent 72%
    );
  }
  &__icon-wrap--orange::before {
    background: radial-gradient(
      circle,
      fade(#ff9a3c, 36%) 0%,
      fade(#ff9a3c, 8%) 50%,
      transparent 72%
    );
  }
  &__icon {
    position: relative;
    z-index: 1;
    font-size: 20 / @rem2;
    line-height: 1;
    color: #7ae7ff;
    filter: drop-shadow(0 0 4px fade(@mainColor, 65%));
  }
  &__icon-wrap--green .kpi__icon {
    color: #36d7b7;
    filter: drop-shadow(0 0 4px fade(#36d7b7, 65%));
  }
  &__icon-wrap--orange .kpi__icon {
    color: #ffb366;
    filter: drop-shadow(0 0 4px fade(#ff9a3c, 65%));
  }
  &__label {
    color: @assistColor;
    font-size: 14 / @rem2;
    white-space: nowrap;
  }
  b {
    font-size: 24 / @rem2;
    color: #fff;
    flex-shrink: 0;
    em {
      font-style: normal;
      font-size: 12 / @rem2;
      margin-left: 4 / @rem2;
      color: fade(#fff, 55%);
    }
  }
  &--green {
    border-color: fade(#36d7b7, 45%);
    b {
      color: #36d7b7;
    }
  }
  &--orange {
    border-color: fade(#ff9a3c, 45%);
    b {
      color: #ffb366;
    }
  }
}
.cols {
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 10 / @rem2;
}
.col {
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 10 / @rem2;
}
.panel {
  padding: 12 / @rem2;
  border: 1 / @rem2 solid fade(@assistColor, 22%);
  border-radius: 6 / @rem2;
  background: rgba(6, 22, 52, 0.55);
  display: flex;
  flex-direction: column;
  min-height: 0;
  h3 {
    margin: 0 0 10 / @rem2;
    color: @mainColor;
    font-size: 16 / @rem2;
  }
}
.panel--rank,
.panel--chart {
  flex: 1;
  min-height: 220 / @rem2;
}
.chart {
  flex: 1;
  min-height: 200 / @rem2;
  width: 100%;
  height: 0;
}
.flow {
  display: flex;
  align-items: center;
  gap: 0;
  position: relative;
  z-index: 1;
}
.flow__step {
  display: flex;
  align-items: center;
  flex: 1 1 0;
  min-width: 0;
}
.flow__node {
  flex: 1 1 0;
  min-width: 0;
  position: relative;
  text-align: center;
  border: 1 / @rem2 solid fade(@mainColor, 35%);
  border-radius: 8 / @rem2;
  padding: 12 / @rem2 6 / @rem2;
  background: rgba(0, 80, 150, 0.28);
  color: #e8f3ff;
  cursor: pointer;
  transition: box-shadow 0.25s ease, border-color 0.25s ease;
  strong {
    display: block;
    font-size: 13 / @rem2;
    letter-spacing: 0.5 / @rem2;
    white-space: nowrap;
  }
  em {
    display: block;
    margin-top: 8 / @rem2;
    font-style: normal;
    font-size: 22 / @rem2;
    font-weight: 700;
    line-height: 1.1;
    color: #fff;
  }
  span {
    display: block;
    margin-top: 2 / @rem2;
    font-size: 11 / @rem2;
    color: fade(#fff, 55%);
  }
  &.tone-red {
    border-color: fade(#ff5a5a, 70%);
    box-shadow: inset 0 0 12 / @rem2 fade(#ff5a5a, 12%);
    em {
      color: #ff8a8a;
    }
  }
  &.tone-blue {
    border-color: fade(#00c3ff, 70%);
    box-shadow: inset 0 0 12 / @rem2 fade(#00c3ff, 12%);
    em {
      color: #5ad6ff;
    }
  }
  &.tone-green {
    border-color: fade(#36d7b7, 70%);
    box-shadow: inset 0 0 12 / @rem2 fade(#36d7b7, 12%);
    em {
      color: #36d7b7;
    }
  }
  &.tone-gold {
    border-color: fade(#f5c542, 70%);
    box-shadow: inset 0 0 12 / @rem2 fade(#f5c542, 12%);
    em {
      color: #f5c542;
    }
  }
  &:hover {
    box-shadow: 0 0 14 / @rem2 fade(@mainColor, 35%);
  }
}
.flow__link {
  position: relative;
  flex: 0 0 28 / @rem2;
  height: 14 / @rem2;
  margin: 0 2 / @rem2;
  overflow: hidden;
}
.flow__link-track,
.flow__link-beam {
  display: block;
  position: absolute;
  left: 0;
  right: 0;
  top: 50%;
  height: 2 / @rem2;
  margin-top: -1 / @rem2;
  border-radius: 1 / @rem2;
}
.flow__link-track {
  background: linear-gradient(
    90deg,
    fade(#00c3ff, 15%),
    fade(#00c3ff, 55%),
    fade(#7ad7ff, 45%),
    fade(#00c3ff, 15%)
  );
  box-shadow: 0 0 6 / @rem2 fade(#00c3ff, 35%);
}
.flow__link-beam {
  width: 55%;
  background: linear-gradient(
    90deg,
    transparent 0%,
    fade(#9ee7ff, 25%) 25%,
    #c8f2ff 50%,
    fade(#9ee7ff, 25%) 75%,
    transparent 100%
  );
  filter: blur(0.4 / @rem2);
  animation: flowBeam 1.8s linear infinite;
}
.flow__step:nth-child(2) .flow__link-beam {
  animation-delay: 0.35s;
}
.flow__step:nth-child(3) .flow__link-beam {
  animation-delay: 0.7s;
}
@keyframes flowBeam {
  0% {
    transform: translateX(-120%);
    opacity: 0.35;
  }
  20% {
    opacity: 1;
  }
  80% {
    opacity: 1;
  }
  100% {
    transform: translateX(200%);
    opacity: 0.35;
  }
}
.rank {
  list-style: none;
  margin: 0;
  padding: 0;
  overflow: auto;
  flex: 1;
  min-height: 0;
  li {
    display: flex;
    align-items: center;
    gap: 10 / @rem2;
    padding: 8 / @rem2 10 / @rem2;
    margin-bottom: 6 / @rem2;
    border-radius: 6 / @rem2;
    background: rgba(255, 255, 255, 0.06);
    border: 1 / @rem2 solid fade(#fff, 10%);
    cursor: pointer;
    color: #e8f3ff;
  }
  &__no {
    width: 22 / @rem2;
    text-align: center;
    font-weight: 700;
    color: fade(#fff, 55%);
    flex-shrink: 0;
  }
  &__medal {
    position: relative;
    width: 26 / @rem2;
    height: 26 / @rem2;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 13 / @rem2;
    font-weight: 800;
    line-height: 1;
    overflow: hidden;
    box-shadow: 0 2 / @rem2 8 / @rem2 rgba(0, 0, 0, 0.35);
    &::after {
      content: "";
      position: absolute;
      top: -30%;
      left: -60%;
      width: 45%;
      height: 160%;
      background: linear-gradient(
        105deg,
        transparent 0%,
        rgba(255, 255, 255, 0.55) 45%,
        transparent 70%
      );
      transform: skewX(-18deg);
      animation: medalShine 2.8s ease-in-out infinite;
      pointer-events: none;
    }
  }
  &__avatar {
    width: 32 / @rem2;
    height: 32 / @rem2;
    border-radius: 50%;
    background: rgba(0, 195, 255, 0.28) center/cover no-repeat;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    font-size: 13 / @rem2;
    color: #9fd4ff;
    overflow: hidden;
  }
  &__meta {
    flex: 1;
    min-width: 0;
    strong {
      display: block;
      font-size: 14 / @rem2;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    em {
      display: block;
      font-style: normal;
      font-size: 11 / @rem2;
      color: fade(#fff, 45%);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
  b {
    color: @mainColor;
    font-size: 16 / @rem2;
    flex-shrink: 0;
  }
  .medal-1 {
    border-color: fade(#f5c542, 70%);
    background: linear-gradient(
      90deg,
      rgba(200, 150, 40, 0.38),
      rgba(255, 255, 255, 0.05)
    );
    box-shadow: inset 0 0 12 / @rem2 rgba(245, 197, 66, 0.12);
    .rank__medal {
      color: #3a2a08;
      background: linear-gradient(145deg, #ffe9a0 0%, #f5c542 42%, #c9921a 100%);
      border: 1 / @rem2 solid #ffe9a0;
      box-shadow:
        0 0 10 / @rem2 rgba(245, 197, 66, 0.45),
        inset 0 1 / @rem2 2 / @rem2 rgba(255, 255, 255, 0.55);
    }
    b {
      color: #f5c542;
    }
  }
  .medal-2 {
    border-color: fade(#c8d0e0, 70%);
    background: linear-gradient(
      90deg,
      rgba(160, 170, 190, 0.3),
      rgba(255, 255, 255, 0.05)
    );
    box-shadow: inset 0 0 12 / @rem2 rgba(200, 210, 230, 0.1);
    .rank__medal {
      color: #2a3344;
      background: linear-gradient(145deg, #f2f5fa 0%, #c0c8d8 45%, #8e99ad 100%);
      border: 1 / @rem2 solid #e8eef8;
      box-shadow:
        0 0 10 / @rem2 rgba(192, 200, 216, 0.4),
        inset 0 1 / @rem2 2 / @rem2 rgba(255, 255, 255, 0.65);
    }
    b {
      color: #d0d8e8;
    }
  }
  .medal-3 {
    border-color: fade(#d08a55, 70%);
    background: linear-gradient(
      90deg,
      rgba(160, 100, 50, 0.32),
      rgba(255, 255, 255, 0.05)
    );
    box-shadow: inset 0 0 12 / @rem2 rgba(208, 138, 85, 0.12);
    .rank__medal {
      color: #3a220f;
      background: linear-gradient(145deg, #f0c49a 0%, #d08a55 45%, #9a5a30 100%);
      border: 1 / @rem2 solid #f0c49a;
      box-shadow:
        0 0 10 / @rem2 rgba(208, 138, 85, 0.4),
        inset 0 1 / @rem2 2 / @rem2 rgba(255, 230, 200, 0.5);
    }
    b {
      color: #e0a070;
    }
  }
}
@keyframes medalShine {
  0%,
  35% {
    left: -70%;
    opacity: 0;
  }
  45% {
    opacity: 1;
  }
  70% {
    left: 120%;
    opacity: 0.2;
  }
  100% {
    left: 120%;
    opacity: 0;
  }
}
.panel--honor {
  flex: 1;
  min-height: 0;
  overflow: hidden;
}
.honor-carousel {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 8 / @rem2;
}
.honor-carousel__viewport {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  border-radius: 6 / @rem2;
}
.honor-carousel__track {
  display: flex;
  height: 100%;
  transition: transform 0.55s ease;
  will-change: transform;
}
.honor-slide {
  flex: 0 0 100%;
  width: 100%;
  min-width: 0;
  height: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  text-align: left;
  border: 1 / @rem2 solid fade(#fff, 12%);
  border-radius: 6 / @rem2;
  padding: 0;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.06);
  color: #e8f3ff;
  cursor: pointer;
  &__img {
    flex: 1;
    min-height: 110 / @rem2;
    min-width: 0;
    background: rgba(0, 90, 150, 0.4) center / cover no-repeat;
  }
  &__body {
    flex-shrink: 0;
    min-width: 0;
    width: 100%;
    box-sizing: border-box;
    padding: 10 / @rem2 12 / @rem2 12 / @rem2;
    strong {
      display: block;
      max-width: 100%;
      font-size: 14 / @rem2;
      line-height: 1.35;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    p {
      margin: 6 / @rem2 0 0;
      max-width: 100%;
      font-size: 12 / @rem2;
      color: fade(#fff, 62%);
      line-height: 1.45;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    &.is-large {
      strong {
        font-size: 17 / @rem2;
      }
      p {
        font-size: 13 / @rem2;
      }
    }
  }
  &__cat {
    display: inline-block;
    margin: 0 0 4 / @rem2;
    padding: 1 / @rem2 6 / @rem2;
    border-radius: 3 / @rem2;
    border: 1 / @rem2 solid fade(@mainColor, 40%);
    color: @mainColor;
    font-size: 11 / @rem2;
    font-style: normal;
  }
}
.honor-carousel__dots {
  display: flex;
  justify-content: center;
  gap: 6 / @rem2;
  flex-shrink: 0;
}
.honor-carousel__dot {
  width: 7 / @rem2;
  height: 7 / @rem2;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: fade(#fff, 28%);
  cursor: pointer;
  &.on {
    background: @mainColor;
    box-shadow: 0 0 6 / @rem2 fade(@mainColor, 55%);
  }
}
.empty {
  color: fade(#fff, 45%);
  font-size: 13 / @rem2;
}
.lb {
  position: fixed;
  inset: 0;
  z-index: 4000;
  background: rgba(0, 0, 0, 0.82);
  display: flex;
  align-items: center;
  justify-content: center;
  &__body {
    max-width: 70vw;
    text-align: center;
    img {
      max-width: 70vw;
      max-height: 75vh;
      object-fit: contain;
    }
    p {
      color: #fff;
      margin-top: 10 / @rem2;
    }
  }
  &__placeholder {
    width: 50vw;
    height: 40vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(20, 60, 110, 0.8);
    color: #fff;
    font-size: 20 / @rem2;
  }
  &__nav {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 48 / @rem2;
    height: 48 / @rem2;
    border: 0;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.15);
    color: #fff;
    font-size: 28 / @rem2;
    cursor: pointer;
  }
  &__prev {
    left: 24 / @rem2;
  }
  &__next {
    right: 24 / @rem2;
  }
  &__close {
    position: absolute;
    top: 20 / @rem2;
    right: 28 / @rem2;
    border: 0;
    background: transparent;
    color: #fff;
    font-size: 32 / @rem2;
    cursor: pointer;
  }
}
</style>
